import { HttpStatusCode } from 'axios';
import { Session, User } from '@supabase/supabase-js';
import { ResponseModel } from '../../models/Response';
import { ApiEndpoints } from '../../config/constants';
import useAuthSession from '../../app/hooks/custom/useAuthSession';
import apiClient from '../api/ApiClient';
import trackingService from '../analytics/TrackingService';
import { userStore } from '../../app/stores/User';

const AuthService = () => {
    const { saveSession, clearSession } = useAuthSession();
    
    const signInWithEmailAndPassword = async (email: string, password: string) => {
        const timestamp = new Date().toISOString();
        console.log(`🔐 [AuthService] ${timestamp} - Sign-in attempt for: ${email}`);
        
        try {
            const { data: { data, statusCode, errorMessage } } = await apiClient.postPublic<ResponseModel<{ user: User, session: Session }>>(
                ApiEndpoints.Auth.SignIn, {
                    email: email,
                    password: password,
                    withCredentials: true,
                }
            );

            if (data?.user && statusCode === HttpStatusCode.Ok) {
                console.log(`✅ [AuthService] Sign-in successful for: ${email}`, {
                    userId: data.user.id,
                    expiresAt: data.session?.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : 'unknown',
                    hasRefreshToken: !!data.session?.refresh_token
                });
                
                setTimeout(() => {
                    saveSession(data.user, data.session);
                    console.log(`📝 [AuthService] Session saved to store for: ${email}`);
                }, 1000);

                // Track sign-in event
                trackingService.trackSignIn(data.user.id).catch(error => {
                    console.error('Failed to track sign-in:', error);
                });

                return {
                    success: true,
                    message: "Signed in successfully",
                };
            }

            console.warn(`❌ [AuthService] Sign-in failed for: ${email}`, {
                statusCode,
                errorMessage
            });

            return {
                success: false,
                message: errorMessage ?? "We're having trouble signing you in. Please try again later.",
            };
        } catch (error) {
            console.error(`🔥 [AuthService] Sign-in error for: ${email}`, error);
            return {
                success: false,
                message: "Error signing in: " + error,
            };
        }
    };

    interface ExistingStore {
        storeId: string;
        storeName: string;
        storeSlug: string;
        storeLogo: string;
        userFirstName: string;
        userLastName: string;
        registeredAt: string;
    }

    interface CheckExistingResponse {
        exists: boolean;
        email?: string;
        stores?: ExistingStore[];
    }

    /**
     * Check if a user already exists on the platform
     */
    const checkExistingUser = async (email: string): Promise<{
        exists: boolean;
        stores?: ExistingStore[];
        error?: string;
    }> => {
        try {
            const { data } = await apiClient.postPublic<ResponseModel<CheckExistingResponse>>(
                '/auth/check-existing',
                { email }
            );

            if (data.statusCode === HttpStatusCode.Ok && data.data) {
                return {
                    exists: data.data.exists,
                    stores: data.data.stores
                };
            }

            return { exists: false, error: data.errorMessage };
        } catch (error: any) {
            console.error('[AuthService] Error checking existing user:', error);
            return { exists: false, error: error.message };
        }
    };

    /**
     * Add store access for an existing user
     */
    const addStoreAccess = async (email: string, password: string): Promise<{
        success: boolean;
        message: string;
        user?: User;
        session?: Session;
    }> => {
        try {
            const { data } = await apiClient.postPublic<ResponseModel<{ success: boolean; user?: User; session?: Session }>>(
                '/auth/add-store-access',
                { email, password }
            );

            if (data.statusCode === HttpStatusCode.Ok && data.data?.success) {
                // If we got user and session data, save it
                if (data.data.user && data.data.session) {
                    console.log('✅ [AuthService] Store access added, signing in user automatically');
                    saveSession(data.data.user, data.data.session);
                }
                
                return {
                    success: true,
                    message: 'Store access added successfully!',
                    user: data.data.user,
                    session: data.data.session
                };
            }

            return {
                success: false,
                message: data.errorMessage || 'Failed to add store access'
            };
        } catch (error: any) {
            console.error('[AuthService] Error adding store access:', error);
            return {
                success: false,
                message: error.response?.data?.errorMessage || 'Something went wrong. Please try again.'
            };
        }
    };

    const createStandardUser = async (email: string, password: string, phone: string, firstName: string, lastName: string): Promise<{
        success: boolean;
        message: string;
        existingUser?: boolean;
        existingStores?: ExistingStore[];
    }> => {
        try {
            const { data: { statusCode, errorMessage } } = await apiClient.postPublic<ResponseModel<{ user: User, session: Session }>>(
                ApiEndpoints.Auth.SignUp, {
                email: email,
                password: password,
                phone: phone,
                firstName: firstName,
                lastName: lastName,
            });
            
            if (statusCode === HttpStatusCode.Created) {
                return {
                    success: true,
                    message: `Done! Please check ${email} for your verification link.`,
                };
            }

            // Check if user already exists on platform
            if (statusCode === HttpStatusCode.Conflict && errorMessage === 'USER_EXISTS_ON_PLATFORM') {
                // Fetch existing user's stores
                const { exists, stores } = await checkExistingUser(email);
                console.log('[AuthService] checkExistingUser result:', { exists, stores });
                
                if (exists) {
                    if (stores && stores.length > 0) {
                        return {
                            success: false,
                            message: 'You already have an account on Cosmo Cart!',
                            existingUser: true,
                            existingStores: stores
                        };
                    } else {
                        // User already has access to this store - tell them to sign in
                        return {
                            success: false,
                            message: 'You already have an account on this store. Please sign in instead.'
                        };
                    }
                }
            }

            return {
                success: false,
                message: errorMessage ?? "We're having trouble signing you up. Please try again later.",
            };
        } catch (error: any) {
            // Check for conflict response (user exists)
            if (error.response?.status === HttpStatusCode.Conflict) {
                const errorMsg = error.response?.data?.errorMessage;
                
                if (errorMsg === 'USER_EXISTS_ON_PLATFORM') {
                    const { exists, stores } = await checkExistingUser(email);
                    console.log('[AuthService] checkExistingUser result (catch):', { exists, stores });
                    
                    if (exists) {
                        if (stores && stores.length > 0) {
                            return {
                                success: false,
                                message: 'You already have an account on Cosmo Cart!',
                                existingUser: true,
                                existingStores: stores
                            };
                        } else {
                            return {
                                success: false,
                                message: 'You already have an account on this store. Please sign in instead.'
                            };
                        }
                    }
                }
            }

            console.error('Error:', error);
            return {
                success: false,
                message: 'Error signing up: ' + (error.response?.data?.errorMessage || error.message),
            };
        }
    };

    const signOut = async () => {
        const timestamp = new Date().toISOString();
        const currentUser = userStore.getState().user;
        
        console.log(`🚪 [AuthService] ${timestamp} - Sign-out initiated for user:`, {
            userId: currentUser.info?.id,
            email: currentUser.info?.email
        });
        
        try {
            // Use authenticated post (not postPublic) since signOut now requires auth
            const { status } = await apiClient.post(ApiEndpoints.Auth.SignOut);
            
            if (status === HttpStatusCode.Ok || status === HttpStatusCode.Created) {
                console.log('✅ [AuthService] Sign-out API call successful');
                
                // Track sign-out event
                trackingService.trackSignOut().catch(error => {
                    console.error('Failed to track sign-out:', error);
                });
                return true;
            }
            
            console.warn('⚠️ [AuthService] Sign-out API call returned unexpected status:', status);
        } catch (error) {
            console.error("❌ [AuthService] Sign-out error:", error);
            // Even if API call fails, clear local session
            return false;
        } finally {
            setTimeout(() => {
                console.log('🗑️ [AuthService] Clearing session from store');
                clearSession();
            }, 1000);
        }

        return false;
    };
    
    const leaveStore = async (): Promise<{
        success: boolean;
        message: string;
    }> => {
        try {
            const { data } = await apiClient.post<ResponseModel<{ success: boolean }>>(
                '/auth/leave-store',
                {}
            );

            if (data.statusCode === HttpStatusCode.Ok && data.data?.success) {
                console.log('✅ [AuthService] Successfully left store');
                return {
                    success: true,
                    message: 'Successfully left store'
                };
            }

            return {
                success: false,
                message: data.errorMessage || 'Failed to leave store'
            };
        } catch (error: any) {
            console.error('[AuthService] Error leaving store:', error);
            return {
                success: false,
                message: error.response?.data?.errorMessage || 'Something went wrong. Please try again.'
            };
        }
    };

    return {
        signOut,
        signInWithEmailAndPassword,
        createStandardUser,
        checkExistingUser,
        addStoreAccess,
        leaveStore
    };
};

export default AuthService;